import React from 'react';
import type { i18n as I18n } from 'i18next';
import { generatePath, useHistory } from 'react-router-dom';

import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { Comment, Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { messageIsFromExtension } from 'flow/utils/message-utils';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { clearSelectedThreadMessage } from 'flow/actions/chat-actions';

interface Props {
    message: TypedMap<Message | Comment>;
    sessionKey: ChatSessionKey;
}

/**
 * The sender as the row shows it: "You" for the own user, else the contact
 * name from the message itself, else Unknown. Also the sender part of the
 * row's accessible name.
 */
export const getMessageSenderName = <T extends Message | Comment>(
    message: TypedMap<T>,
    ownExtensionKey: string,
    i18next: I18n,
) => {
    if (messageIsFromExtension(message, ownExtensionKey)) {
        return i18next.t('chat.you');
    }
    const unknown = i18next.t('Contact.Unknown');
    return message.has('from')
        ? extractName(message.getIn(['from', 'contact']), unknown)
        : unknown;
};

const MessageSender = ({ message, sessionKey }: Props) => {
    const { i18next } = useAppContext();
    const { extension } = useExtension(message.getIn(['from', 'extensionKey']));
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const history = useHistory();
    const { closeModal } = useModal();
    const dispatch = useAppDispatch();

    // The address is read at the click. Reading it with useLocation or
    // useParams would render the sender of every message again on each change
    // of the address.
    const onClick = () => {
        closeModal();
        dispatch(clearSelectedThreadMessage());
        history.push(
            generatePath(routes['messages.id.contact.key'], {
                extensionKey: message.getIn(['from', 'extensionKey']),
                id: sessionKey,
            }),
            { from: history.location.pathname },
        );
    };

    const senderIsMe = messageIsFromExtension(message, ownExtensionKey);
    if (senderIsMe) {
        return (
            <div className="text-chatYou text-start text-sm/4 font-bold">
                {i18next.t('chat.you')}
            </div>
        );
    }

    const senderName = getMessageSenderName(message, ownExtensionKey, i18next);

    return (
        <button
            className="text-start text-sm font-bold"
            onClick={onClick}
            disabled={!extension?.get('key')}
        >
            {senderName}
        </button>
    );
};

export default MessageSender;
